import { CommunityStatus } from "@prisma/client";
import React, { useEffect, useRef, useState } from "react";
import { GoInfo } from "react-icons/go";
import { toast } from "react-toastify";
import { useCommunity } from "~/hooks/useCommunity";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../shadcn/Tooltip";
import { Button } from "../v3/themed/Button";
import { BASE_INPUT_STYLES } from "../v3/themed/BASE_STYLE_DEFINITIONS";

type Props = {
    communitySlug?: string;
    disabled?: boolean;
    className?: React.ComponentProps<"div">["className"];
};

const CommunityStatusDropdown = ({
    communitySlug,
    disabled,
    className,
}: Props) => {
    const inputRef = useRef<HTMLSelectElement>(null);

    const { community, communityLoading, refetch } = useCommunity();
    const updateCommunity = api.community.updateCommunity.useMutation();

    const [inputValue, setInputValue] = useState<CommunityStatus>(
        community?.status ?? CommunityStatus.DRAFT,
    );
    useEffect(() => {
        if (community && !communityLoading) {
            setInputValue(community.status);
        }

        return () => {
            setInputValue(community?.status ?? CommunityStatus.DRAFT);
        };
    }, [community?.status, communityLoading]);

    // Handle change event for input
    const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value as CommunityStatus;
        setInputValue(newValue);

        if (community) {
            let message: string;
            switch (newValue) {
                case CommunityStatus.DRAFT:
                    message = "Successfully converted to draft.";
                    break;
                case CommunityStatus.PREVIEW:
                    message = "Successfully converted to preview.";
                    break;
                case CommunityStatus.PUBLISHED:
                    message = "Successfully published.";
                    break;
                default:
                    message = "Updated community!";
            }

            updateCommunity.mutateAsync(
                {
                    slug: community.slug,
                    status: newValue,
                },
                {
                    onSuccess: async (res) => {
                        if (res) {
                            toast.dismiss("update-community-loading");
                            toast.success(message, {
                                autoClose: 1200,
                            });

                            if (refetch) {
                                refetch();
                            }
                        }
                    },
                    onError: (e) => {
                        toast.dismiss("update-community-loading");
                        console.debug(e);
                    },
                },
            );
        }
    };

    return (
        <div className="flex max-w-[240px] flex-col items-center">
            <div
                className={`relative mb-2 mt-2 flex w-full flex-row ${className} z-50`}
            >
                <select
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={disabled}
                    className={cn(BASE_INPUT_STYLES, "w-full border p-2")}
                >
                    {" "}
                    <option value={CommunityStatus.DRAFT}>
                        Draft (admin only)
                    </option>{" "}
                    <option value={CommunityStatus.PREVIEW}>
                        Preview (direct access only)
                    </option>
                    <option value={CommunityStatus.PUBLISHED}>
                        Published (across the app)
                    </option>
                    <option value={CommunityStatus.WAITLIST}>
                        Waitlist (coming soon)
                    </option>
                </select>

                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger>
                            <GoInfo className="ml-2 h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent sideOffset={-100}>
                            <p>
                                This is the status of the community. If the
                                community is in Draft, people will not be able
                                to access your commuinty by the community link
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {community?.status === CommunityStatus.DRAFT && (
                <div className="flex w-full">
                    <Button
                        className="mb-2 h-max max-w-[240px] rounded py-2"
                        variant="secondary"
                        onClick={() => {
                            window.open(
                                "https://www.getriver.io/pricing",
                                "_blank",
                            );
                        }}
                    >
                        <span className="max-w-[180px]">
                            Subscribe to publish your community
                        </span>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CommunityStatusDropdown;
